import { cva } from 'class-variance-authority'
import { ChangeEvent, InputHTMLAttributes, useCallback, useEffect, useState } from 'react'

import { cn } from '@/utils/style'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  value?: number | string
  disabled?: boolean
  allowClear?: boolean
  onChange?: (value: ChangeEvent<HTMLInputElement>) => void
  onClear?: () => void
  onPressEnter?: (value: string) => void
  validator?: (value: string) => boolean
}

const inputVariants = cva(
  'w-full h-12 leading-12 px-4 rounded-lg border-[2px] border-gray00 outline-none focus:border-secondary',
  {
    variants: {
      disabled: {
        true: 'cursor-not-allowed',
      },
      error: {
        true: 'border-red-500 focus:border-red-500',
      },
    },
  },
)

export function Input({
  value,
  onChange,
  onClear,
  onPressEnter,
  disabled = false,
  allowClear = false,
  validator,
  ...rest
}: InputProps) {
  const [v, setV] = useState(value)
  const [error, setError] = useState(false)

  useEffect(() => {
    setV(value)
  }, [value])

  const onVChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setV(newValue)
      setError(validator ? !validator(newValue) : false)

      if (onChange) {
        onChange(e)
      }
    },
    [onChange],
  )

  const handleClear = useCallback(() => {
    setV('')
    setError(false)
    if (onClear) {
      onClear()
    }
  }, [onClear])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e?.key === 'Enter' && onPressEnter && typeof v === 'string') {
        onPressEnter(v)
      }
    },
    [v, onPressEnter],
  )

  return (
    <div className="relative">
      <input
        value={v}
        onChange={onVChange}
        onKeyDown={handleKeyDown}
        className={cn(inputVariants({ disabled, error }))}
        disabled={disabled}
        {...rest}
      />
      {allowClear && !disabled && v && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      )}
    </div>
  )
}
